/**
 * Browser compatibility utilities and feature detection
 */

export interface BrowserInfo {
  name: string;
  version: string;
  engine: string;
  platform: string;
  mobile: boolean;
  supported: boolean;
  warnings: string[];
}

export interface FeatureSupport {
  webgl: boolean;
  webgl2: boolean;
  webWorkers: boolean;
  serviceWorkers: boolean;
  indexedDB: boolean;
  localStorage: boolean;
  sessionStorage: boolean;
  webSockets: boolean;
  fetch: boolean;
  promises: boolean;
  asyncAwait: boolean;
  modules: boolean;
  intersectionObserver: boolean;
  resizeObserver: boolean;
  mutationObserver: boolean;
  performanceObserver: boolean;
  webVitals: boolean;
  css: {
    grid: boolean;
    flexbox: boolean;
    customProperties: boolean;
    transforms: boolean;
    animations: boolean;
  };
}

class BrowserCompatibilityChecker {
  private userAgent: string;
  private browserInfo: BrowserInfo | null = null;
  private featureSupport: FeatureSupport | null = null;

  constructor() {
    this.userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent : '';
  }

  getBrowserInfo(): BrowserInfo {
    if (this.browserInfo) return this.browserInfo;

    const ua = this.userAgent;
    let name = 'Unknown';
    let version = 'Unknown';
    let engine = 'Unknown';
    const warnings: string[] = [];

    if (ua.includes('Chrome') && !ua.includes('Chromium')) {
      name = 'Chrome';
      const match = ua.match(/Chrome\/(\d+)/);
      version = match ? match[1] : 'Unknown';
      engine = 'Blink';
    } else if (ua.includes('Firefox')) {
      name = 'Firefox';
      const match = ua.match(/Firefox\/(\d+)/);
      version = match ? match[1] : 'Unknown';
      engine = 'Gecko';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      name = 'Safari';
      const match = ua.match(/Version\/(\d+)/);
      version = match ? match[1] : 'Unknown';
      engine = 'WebKit';
    } else if (ua.includes('Edge')) {
      name = 'Edge';
      const match = ua.match(/Edge\/(\d+)/);
      version = match ? match[1] : 'Unknown';
      engine = 'EdgeHTML';
    } else if (ua.includes('Edg/')) {
      name = 'Edge Chromium';
      const match = ua.match(/Edg\/(\d+)/);
      version = match ? match[1] : 'Unknown';
      engine = 'Blink';
    }

    let platform = 'Unknown';
    if (ua.includes('Windows')) platform = 'Windows';
    else if (ua.includes('Mac')) platform = 'macOS';
    else if (ua.includes('Linux')) platform = 'Linux';
    else if (ua.includes('Android')) platform = 'Android';
    else if (ua.includes('iOS')) platform = 'iOS';

    const mobile = /Mobile|Android|iPhone|iPad/.test(ua);

    const supported = this.isBrowserSupported(name, parseInt(version));
    if (!supported) {
      warnings.push(
        `${name} ${version} may not be fully supported. Please update to the latest version.`
      );
    }

    if (name === 'Safari' && parseInt(version) < 14) {
      warnings.push(
        'Safari versions below 14 may have issues with modern JavaScript features.'
      );
    }
    if (name === 'Firefox' && parseInt(version) < 90) {
      warnings.push('Firefox versions below 90 may have performance issues.');
    }
    if (name === 'Chrome' && parseInt(version) < 90) {
      warnings.push('Chrome versions below 90 may have compatibility issues.');
    }

    this.browserInfo = {
      name,
      version,
      engine,
      platform,
      mobile,
      supported,
      warnings,
    };

    return this.browserInfo;
  }

  getFeatureSupport(): FeatureSupport {
    if (this.featureSupport) return this.featureSupport;

    const support: FeatureSupport = {
      webgl: this.checkWebGL(),
      webgl2: this.checkWebGL2(),
      webWorkers: typeof Worker !== 'undefined',
      serviceWorkers: 'serviceWorker' in navigator,
      indexedDB: 'indexedDB' in window,
      localStorage: this.checkLocalStorage(),
      sessionStorage: this.checkSessionStorage(),
      webSockets: 'WebSocket' in window,
      fetch: 'fetch' in window,
      promises: 'Promise' in window,
      asyncAwait: this.checkAsyncAwait(),
      modules: this.checkModules(),
      intersectionObserver: 'IntersectionObserver' in window,
      resizeObserver: 'ResizeObserver' in window,
      mutationObserver: 'MutationObserver' in window,
      performanceObserver: 'PerformanceObserver' in window,
      webVitals: this.checkWebVitals(),
      css: {
        grid: this.checkCSSFeature('display', 'grid'),
        flexbox: this.checkCSSFeature('display', 'flex'),
        customProperties: this.checkCSSCustomProperties(),
        transforms: this.checkCSSFeature('transform', 'translateX(1px)'),
        animations: this.checkCSSFeature('animation', 'none'),
      },
    };

    this.featureSupport = support;
    return support;
  }

  private isBrowserSupported(name: string, version: number): boolean {
    const minVersions: Record<string, number> = {
      Chrome: 88,
      Firefox: 85,
      Safari: 14,
      Edge: 88,
      'Edge Chromium': 88,
    };

    return minVersions[name] ? version >= minVersions[name] : false;
  }

  private checkWebGL(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!(
        canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      );
    } catch {
      return false;
    }
  }

  private checkWebGL2(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!canvas.getContext('webgl2');
    } catch {
      return false;
    }
  }

  private checkLocalStorage(): boolean {
    try {
      const test = '__test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  private checkSessionStorage(): boolean {
    try {
      const test = '__test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  private checkAsyncAwait(): boolean {
    try {
      // eslint-disable-next-line no-new-func
      new Function('return (async () => {})')();
      return true;
    } catch {
      return false;
    }
  }

  private checkModules(): boolean {
    const script = document.createElement('script');
    return 'noModule' in script;
  }

  private checkWebVitals(): boolean {
    return 'PerformanceObserver' in window && 'PerformanceEntry' in window;
  }

  private checkCSSFeature(property: string, value: string): boolean {
    try {
      const element = document.createElement('div');
      element.style.setProperty(property, value);
      return element.style.getPropertyValue(property) === value;
    } catch {
      return false;
    }
  }

  private checkCSSCustomProperties(): boolean {
    try {
      const element = document.createElement('div');
      element.style.setProperty('--test', 'test');
      return element.style.getPropertyValue('--test') === 'test';
    } catch {
      return false;
    }
  }

  generateCompatibilityReport(): string {
    const browserInfo = this.getBrowserInfo();
    const featureSupport = this.getFeatureSupport();

    const report = {
      timestamp: new Date().toISOString(),
      browser: browserInfo,
      features: featureSupport,
      recommendations: this.generateRecommendations(
        browserInfo,
        featureSupport
      ),
    };

    return JSON.stringify(report, null, 2);
  }

  private generateRecommendations(
    browserInfo: BrowserInfo,
    features: FeatureSupport
  ): string[] {
    const recommendations: string[] = [];

    if (!browserInfo.supported) {
      recommendations.push(
        'Update your browser to the latest version for the best experience.'
      );
    }

    if (!features.webWorkers) {
      recommendations.push(
        'Web Workers are not supported. Some background processing may be slower.'
      );
    }

    if (!features.serviceWorkers) {
      recommendations.push(
        'Service Workers are not supported. Offline functionality will be limited.'
      );
    }

    if (!features.localStorage) {
      recommendations.push(
        'Local Storage is not available. Settings and preferences cannot be saved.'
      );
    }

    if (!features.css.grid) {
      recommendations.push(
        'CSS Grid is not supported. Layout may appear differently.'
      );
    }

    if (!features.css.flexbox) {
      recommendations.push(
        'CSS Flexbox is not supported. Some layouts may not work correctly.'
      );
    }

    if (!features.performanceObserver) {
      recommendations.push(
        'Performance monitoring is not available in this browser.'
      );
    }

    return recommendations;
  }
}

export const browserCompatibility = new BrowserCompatibilityChecker();

export const loadPolyfills = async (): Promise<void> => {
  const features = browserCompatibility.getFeatureSupport();
  const polyfills: Promise<any>[] = [];

  if (!features.fetch) {
    polyfills.push(import('whatwg-fetch'));
  }

  if (!features.intersectionObserver) {
    polyfills.push(import('intersection-observer'));
  }

  if (!features.resizeObserver) {
    polyfills.push(
      import('resize-observer-polyfill').then((module) => {
        if (!window.ResizeObserver) {
          window.ResizeObserver = module.default;
        }
      })
    );
  }

  if (polyfills.length > 0) {
    await Promise.all(polyfills);
    console.log(
      `Loaded ${polyfills.length} polyfills for browser compatibility`
    );
  }
};

export const getBrowserWarnings = (): string[] => {
  const browserInfo = browserCompatibility.getBrowserInfo();
  const features = browserCompatibility.getFeatureSupport();

  const warnings: string[] = [...browserInfo.warnings];

  if (!features.localStorage) {
    warnings.push(
      'Local storage is not available. Some features may not work properly.'
    );
  }

  if (!features.webSockets) {
    warnings.push(
      'WebSocket support is not available. Real-time features may not work.'
    );
  }

  return warnings;
};
